import { useMemo } from "react";
import {
  Box,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import {
  V2_SEARCH_PAGE_SIZES,
  V2_SEARCH_SORTS,
  type EmptyReason,
  type V2SearchPageSize,
  type V2SearchResponse,
  type V2SearchResult,
  type V2SearchSort,
} from "../../api/v2Search";
import type { V2SearchListState } from "../../utils/v2SearchParams";
import {
  PropertyTypeTag,
  Tag,
  formatDate,
  rightTone,
} from "../AllMattersV2Parts";

/** 確定でない（要確認・判定不能）か。未解析の null は含めない */
const unsettled = (status: string | null): boolean =>
  status != null && status !== "確定";

/**
 * 確定でない確度と理由を示す。確定・未解析は印を出さない。
 */
export const JudgementMark = ({
  status,
  reason,
}: {
  status: string | null;
  reason?: string | null;
}): JSX.Element | null => {
  if (!unsettled(status)) return null;
  const mark = (
    <Tag label={status === "判定不能" ? "判定不能" : "要確認"} tone="warning" />
  );
  return reason ? (
    <Tooltip title={reason} describeChild enterTouchDelay={0}>
      <span
        tabIndex={0}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {mark}
      </span>
    </Tooltip>
  ) : (
    mark
  );
};

/** 空の理由ごとの語。所有者がないのか、担保権がないのかで言い方を変える */
const EMPTY_WORDS: Record<EmptyReason, { owner: string; security: string }> = {
  記録なし: { owner: "なし", security: "なし" },
  所有権の登記なし: {
    owner: "所有権の登記なし",
    security: "なし（所有権の登記なし）",
  },
  共用部分: { owner: "規約共用部分", security: "なし（規約共用部分）" },
  敷地権: { owner: "敷地権（所有者は区分建物の登記で確認）", security: "不明" },
  不明: { owner: "不明", security: "不明" },
};

/**
 * 値が空のときの語。空の理由があればそれに従い、旧版の結果（理由なし）は
 * 確定でなければ「ない」とは言えないので「不明」
 */
const emptyLabel = (
  status: string | null,
  reason: EmptyReason | null,
  kind: "owner" | "security",
  none: string
): string =>
  reason ? EMPTY_WORDS[reason][kind] : unsettled(status) ? "不明" : none;

/** スマホのカードではツールチップを開けないので、要確認の理由を下に出す */
const ReasonNote = ({
  status,
  reason,
}: {
  status: string | null;
  reason: string | null | undefined;
}): JSX.Element | null =>
  unsettled(status) && reason ? (
    <Typography variant="caption" color="text.secondary" component="div">
      {status === "判定不能" ? "判定不能" : "要確認"}: {reason}
    </Typography>
  ) : null;

export const decimal = (value: string | null): string => {
  if (value == null) return "";
  const match = /^(-?)(\d+)(\.\d+)?$/.exec(value);
  return match
    ? `${match[1]}${match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${
        match[3] ?? ""
      }`
    : value;
};

/** 円単位の金額を丸めず表示する。 */
export const formatManYen = (yen: number | null): string => {
  if (yen == null) return "";
  return `${yen.toLocaleString("ja-JP")}円`;
};

/** 登記の所在は市区町村から始まることが多いので、足りない頭だけを補う */
export const resultAddress = (row: V2SearchResult): string => {
  const prefecture = row.prefecture ?? "";
  const city = row.city ?? "";
  const location = row.location ?? "";
  if (prefecture && location.startsWith(prefecture)) return location;
  if (city && location.startsWith(city)) return `${prefecture}${location}`;
  return `${prefecture}${city}${location}`;
};

const AreaCell = ({ row }: { row: V2SearchResult }): JSX.Element => {
  const land = row.kind === "土地";
  const value = land ? row.landAreaM2 : row.floorAreaM2;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Tooltip title={value != null ? `${decimal(value)}㎡` : ""}>
        <Typography
          variant="body2"
          tabIndex={0}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {value != null ? `${decimal(value)}㎡` : "—"}
        </Typography>
      </Tooltip>
      {land && row.landAreaSource === "旧単位換算" && (
        <Tag label="旧換算" tone="info" />
      )}
    </Stack>
  );
};

const OwnersCell = ({ row }: { row: V2SearchResult }): JSX.Element => {
  const { names, count } = row.currentOwners;
  const rest = count - Math.min(names.length, 1);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
      <Typography variant="body2" noWrap title={names.join("、")}>
        {names[0] ??
          emptyLabel(
            row.ownerJudgementStatus,
            row.ownerEmptyReason,
            "owner",
            "—"
          )}
        {rest > 0 ? ` 他${rest}名` : ""}
      </Typography>
      <JudgementMark
        status={row.ownerJudgementStatus}
        reason={row.ownerJudgementReason}
      />
    </Stack>
  );
};

const SecuritiesCell = ({ row }: { row: V2SearchResult }): JSX.Element => {
  const { count, top } = row.activeSecurities;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
      {count === 0 || top == null ? (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel(
            row.securityJudgementStatus,
            row.securityEmptyReason,
            "security",
            "なし"
          )}
        </Typography>
      ) : (
        <>
          <Tag
            label={top.rightKind ?? "担保権"}
            tone={rightTone(top.rightKind)}
          />
          <Tooltip
            title={[top.holder, formatManYen(top.currentAmountYen)]
              .filter(Boolean)
              .join(" ")}
          >
            <Typography
              variant="body2"
              noWrap
              tabIndex={0}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {formatManYen(top.currentAmountYen)}
              {count > 1 ? ` 他${count - 1}件` : ""}
            </Typography>
          </Tooltip>
        </>
      )}
      <JudgementMark
        status={row.securityJudgementStatus}
        reason={row.securityJudgementReason}
      />
    </Stack>
  );
};

const LocationCell = ({ row }: { row: V2SearchResult }): JSX.Element => (
  <Box minWidth={0}>
    <Typography variant="body2" noWrap title={resultAddress(row)}>
      {resultAddress(row) || "所在不明"}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap component="div">
      {row.number ?? ""}
    </Typography>
  </Box>
);

const KindCell = ({ row }: { row: V2SearchResult }): JSX.Element => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
    {row.kind && <PropertyTypeTag type={row.kind} />}
    {row.isClosed && <Tag label="閉鎖" tone="error" filled />}
  </Stack>
);

type CellParams = GridRenderCellParams<unknown, V2SearchResult>;

const baseColumns: Array<GridColDef<V2SearchResult>> = [
  {
    field: "kind",
    headerName: "種別",
    width: 110,
    renderCell: ({ row }: CellParams) => <KindCell row={row} />,
  },
  {
    field: "location",
    headerName: "所在・地番／家屋番号",
    flex: 2,
    minWidth: 200,
    renderCell: ({ row }: CellParams) => <LocationCell row={row} />,
  },
  {
    field: "buildingName",
    headerName: "建物の名称",
    flex: 1,
    minWidth: 140,
    valueGetter: ({ row }) =>
      [row.buildingName, row.unitName].filter(Boolean).join(" "),
  },
  {
    field: "area",
    headerName: "地積・床面積",
    width: 150,
    renderCell: ({ row }: CellParams) => <AreaCell row={row} />,
  },
  {
    field: "newConstructionDate",
    headerName: "新築",
    width: 100,
    valueGetter: ({ row }) => formatDate(row.newConstructionDate),
  },
  {
    field: "owners",
    headerName: "所有者",
    flex: 1.2,
    minWidth: 200,
    renderCell: ({ row }: CellParams) => <OwnersCell row={row} />,
  },
  {
    field: "securities",
    headerName: "現在の担保権",
    flex: 1.5,
    minWidth: 230,
    renderCell: ({ row }: CellParams) => <SecuritiesCell row={row} />,
  },
  {
    field: "dispositionRestrictions",
    headerName: "処分制限",
    width: 110,
    renderCell: ({ row }: CellParams) => (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {row.dispositionRestrictions.map((kind) => (
          <Tag key={kind} label={kind} tone="error" />
        ))}
      </Stack>
    ),
  },
  {
    field: "pictureAcquiredAt",
    headerName: "取得日",
    width: 100,
    valueGetter: ({ row }) =>
      row.pictureAcquiredAt
        ? formatDate(row.pictureAcquiredAt.slice(0, 10))
        : "",
  },
];
// The order is chosen with the 並び select and sent to the API.
const columns = baseColumns.map((column) => ({ ...column, sortable: false }));

/** 件数の表示。下限のときは「1,000件以上」 */
export const totalLabel = (total: V2SearchResponse["total"]): string =>
  `${total.value.toLocaleString("ja-JP")}件${total.isLowerBound ? "以上" : ""}`;

const ResultCard = ({
  row,
  onOpen,
}: {
  row: V2SearchResult;
  onOpen: (row: V2SearchResult) => void;
}): JSX.Element => (
  <Paper variant="outlined">
    <ButtonBase
      onClick={() => {
        onOpen(row);
      }}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        p: 1.5,
        minHeight: MIN_TAP_TARGET,
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center">
          <KindCell row={row} />
          <Typography variant="caption" color="text.secondary" ml="auto">
            取得{" "}
            {row.pictureAcquiredAt
              ? formatDate(row.pictureAcquiredAt.slice(0, 10))
              : "—"}
          </Typography>
        </Stack>
        <LocationCell row={row} />
        {[row.buildingName, row.unitName].some(Boolean) && (
          <Typography variant="body2">
            {[row.buildingName, row.unitName].filter(Boolean).join(" ")}
          </Typography>
        )}
        <AreaCell row={row} />
        <Box>
          <OwnersCell row={row} />
          <ReasonNote
            status={row.ownerJudgementStatus}
            reason={row.ownerJudgementReason}
          />
        </Box>
        <Box>
          <SecuritiesCell row={row} />
          <ReasonNote
            status={row.securityJudgementStatus}
            reason={row.securityJudgementReason}
          />
        </Box>
        {row.dispositionRestrictions.length > 0 && (
          <Stack direction="row" spacing={0.5}>
            {row.dispositionRestrictions.map((kind) => (
              <Tag key={kind} label={kind} tone="error" />
            ))}
          </Stack>
        )}
      </Stack>
    </ButtonBase>
  </Paper>
);

export const V2SearchResults = ({
  data,
  loading,
  error = false,
  listState,
  onListStateChange,
  onOpen,
  isMobile,
}: {
  data: V2SearchResponse | undefined;
  loading: boolean;
  error?: boolean;
  listState: V2SearchListState;
  onListStateChange: (next: V2SearchListState) => void;
  onOpen: (row: V2SearchResult) => void;
  isMobile: boolean;
}): JSX.Element => {
  const rows = data?.results ?? [];
  // A lower-bound total cannot size the pager; -1 lets TablePagination page on has_next.
  const count =
    data == null
      ? 0
      : data.total.isLowerBound
      ? data.hasNext
        ? -1
        : (listState.page - 1) * listState.pageSize + rows.length
      : data.total.value;
  const pagination = (
    <TablePagination
      component="div"
      count={count}
      page={listState.page - 1}
      rowsPerPage={listState.pageSize}
      rowsPerPageOptions={[...V2_SEARCH_PAGE_SIZES]}
      labelRowsPerPage="表示件数"
      labelDisplayedRows={({ from, to }) =>
        data
          ? `${from.toLocaleString("ja-JP")}–${to.toLocaleString(
              "ja-JP"
            )} / ${totalLabel(data.total)}`
          : ""
      }
      nextIconButtonProps={{ disabled: loading || !data?.hasNext }}
      backIconButtonProps={{ disabled: loading || listState.page <= 1 }}
      onPageChange={(_, page) => {
        onListStateChange({ ...listState, page: page + 1 });
      }}
      onRowsPerPageChange={(event) => {
        onListStateChange({
          ...listState,
          page: 1,
          pageSize: Number(event.target.value) as V2SearchPageSize,
        });
      }}
    />
  );
  const sortOptions = useMemo(() => V2_SEARCH_SORTS, []);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          component="p"
          variant="subtitle1"
          fontWeight="bold"
          aria-live="polite"
        >
          {data ? totalLabel(data.total) : loading ? "検索中" : ""}
        </Typography>
        <TextField
          select
          size="small"
          label="並び"
          value={listState.sort}
          onChange={(event) => {
            onListStateChange({
              ...listState,
              page: 1,
              sort: event.target.value as V2SearchSort,
            });
          }}
          sx={{ minWidth: 200 }}
        >
          {sortOptions.map((sort) => (
            <MenuItem key={sort.value} value={sort.value}>
              {sort.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={listState.onePerProperty}
              onChange={(event) => {
                onListStateChange({
                  ...listState,
                  page: 1,
                  onePerProperty: event.target.checked,
                });
              }}
            />
          }
          label="同じ物件は最新の取得分だけ表示"
        />
      </Stack>
      {listState.onePerProperty && (
        <Typography variant="body2" color="text.secondary">
          物件ごとに最新の取得分だけを検索します。古い取得分だけが条件に一致する物件は表示されません。
        </Typography>
      )}
      {data?.total.isLowerBound && (
        <Typography variant="body2" color="text.secondary">
          件数が多いため、{data.total.value.toLocaleString("ja-JP")}
          件までしか数えていません。条件を追加すると正確な件数を表示できます。
        </Typography>
      )}
      <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
      {isMobile ? (
        <Stack spacing={1}>
          {rows.map((row) => (
            <ResultCard key={row.detailId} row={row} onOpen={onOpen} />
          ))}
          {(error || (data && rows.length === 0)) && (
            <Typography color="text.secondary">
              {error
                ? "検索結果を取得できませんでした。"
                : "条件に一致する結果はありません。"}
            </Typography>
          )}
        </Stack>
      ) : (
        <Box sx={{ width: "100%" }}>
          <DataGridPro
            autoHeight
            rows={rows}
            columns={columns}
            getRowId={(row) => row.detailId}
            hideFooter
            disableColumnMenu
            disableSelectionOnClick
            rowHeight={56}
            localeText={{
              noRowsLabel: error
                ? "検索結果を取得できませんでした。"
                : !data && loading
                ? "検索中"
                : "条件に一致する結果はありません",
            }}
            onRowClick={(params) => {
              onOpen(params.row as V2SearchResult);
            }}
            onCellKeyDown={(params, event) => {
              if (event.key === "Enter") onOpen(params.row as V2SearchResult);
            }}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      )}
      {pagination}
    </Stack>
  );
};
